import React, { useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from './ui/select';
import { diffText } from '../lib/diffText';

// Una LINEA por bloque top-level. extractTextFromContent aplana todo el doc a una
// sola linea (join con espacios), inutil para diff por lineas -> caminamos bloques.
function sheetToText(content) {
  if (!content) return '';
  if (typeof content === 'string') return content;
  if (content.type === 'doc' && Array.isArray(content.content)) {
    const blockText = (node) => {
      if (!node) return '';
      if (node.text) return node.text;
      if (Array.isArray(node.content)) return node.content.map(blockText).join('');
      return '';
    };
    return content.content.map(blockText).join('\n');
  }
  return '';
}

const CELL = 'flex-1 px-2 py-0.5 whitespace-pre-wrap break-words text-xs';

const SheetDiffView = ({ open, onOpenChange, sheets = [], initialLeftId }) => {
  const ids = sheets.map((s) => s.id);
  const [leftId, setLeftId] = useState(
    initialLeftId && ids.includes(initialLeftId) ? initialLeftId : (ids[0] || '')
  );
  const [rightId, setRightId] = useState(ids[1] || ids[0] || '');
  const [onlyDiffs, setOnlyDiffs] = useState(false);

  const left = sheets.find((s) => s.id === leftId);
  const right = sheets.find((s) => s.id === rightId);

  const hunks = useMemo(
    () => diffText(sheetToText(left && left.content), sheetToText(right && right.content)),
    [left, right]
  );
  const diffCount = hunks.filter((h) => h.type !== 'equal').length;
  const rows = onlyDiffs ? hunks.filter((h) => h.type !== 'equal') : hunks;

  const leftCls = (h) =>
    h.type === 'removed'
      ? 'bg-red-100 dark:bg-red-900/40 text-red-900 dark:text-red-200'
      : h.type === 'added'
        ? 'bg-slate-50 dark:bg-slate-800/40'
        : 'text-slate-700 dark:text-slate-200';
  const rightCls = (h) =>
    h.type === 'added'
      ? 'bg-green-100 dark:bg-green-900/40 text-green-900 dark:text-green-200'
      : h.type === 'removed'
        ? 'bg-slate-50 dark:bg-slate-800/40'
        : 'text-slate-700 dark:text-slate-200';

  const sheetSelect = (value, onChange, testid, placeholder) => (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {sheets.map((s) => (
          <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass border-0 max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Comparar hojas</DialogTitle>
        </DialogHeader>

        <div className="flex gap-2">
          <div className="flex-1">{sheetSelect(leftId, setLeftId, 'diff-select-left', 'Hoja izquierda')}</div>
          <div className="flex-1">{sheetSelect(rightId, setRightId, 'diff-select-right', 'Hoja derecha')}</div>
        </div>

        <div
          className="flex-1 overflow-auto border border-slate-200 dark:border-slate-700 rounded mt-2 font-mono"
          data-testid="diff-rows"
        >
          {rows.length === 0 ? (
            <div className="p-3 text-sm text-slate-500">Sin contenido para comparar.</div>
          ) : (
            rows.map((h, idx) => (
              <div key={idx} className="flex border-b border-slate-100 dark:border-slate-800">
                <div className={`${CELL} border-r border-slate-100 dark:border-slate-800 ${leftCls(h)}`}>{h.left || ''}</div>
                <div className={`${CELL} ${rightCls(h)}`}>{h.right || ''}</div>
              </div>
            ))
          )}
        </div>

        <div className="flex items-center justify-between mt-2 text-sm text-slate-600 dark:text-slate-300">
          <span data-testid="diff-count">{diffCount} diferencia{diffCount === 1 ? '' : 's'}</span>
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={onlyDiffs} onChange={(e) => setOnlyDiffs(e.target.checked)} />
            Solo diferencias
          </label>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SheetDiffView;
